import React, { useEffect, useMemo, useState } from 'react'
import axios from 'axios'
import {
  Alert,
  Box,
  Button,
  Chip,
  Paper,
  Popover,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  IconButton,
  Collapse,
  Card,
} from '@mui/material'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";

const defaultBrands = ['ADBRY', 'ANZUPGO']
const defaultSources = ['SP', 'Copay NBRx', 'HUB']

const getToday = () => new Date().toISOString().split('T')[0]

const formatNumber = (value) => {
  if (value === null || value === undefined || value === '') return null
  const numeric = Number(value)
  if (Number.isNaN(numeric)) return null
  return new Intl.NumberFormat('en-US').format(numeric)
}

const formatDisplayDate = (yyyyMmDd) => {
  if (!yyyyMmDd) return ''
  const parsed = new Date(`${yyyyMmDd}T00:00:00`)
  if (Number.isNaN(parsed.getTime())) return yyyyMmDd
  return new Intl.DateTimeFormat('en-US', { year: 'numeric', month: 'short', day: '2-digit' }).format(parsed)
}

const parseParentsCsv = (csv) => {
  if (!csv || typeof csv !== 'string') return []
  return csv
    .split(',')
    .map((value) => value.trim())
    .filter(Boolean)
}

const CompareTab = () => {
  const today = useMemo(() => getToday(), [])
  const [pharmaDetails, setPharmaDetails] = useState({ brands: [], sources: [] })
  const [selectedBrand, setSelectedBrand] = useState('ADBRY')
  const [asOfDate, setAsOfDate] = useState(today)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [comparisonResponse, setComparisonResponse] = useState(null)
  const [periods, setPeriods] = useState([])
  const [expandedSources, setExpandedSources] = useState({})

  const toggleSource = (source) => {
    setExpandedSources((prev) => ({
      ...prev,
      [source]: !prev[source],
    }))
  }
  const PERIOD_COL_WIDTH = 160
  const DATE_COL_WIDTH = 130
  const NUMBER_COL_MIN_WIDTH = 110
  const PARENTS_COL_MIN_WIDTH = 280

const tableHeaderCellSx = {
  fontWeight: 700,
  fontSize: 12,
  color: '#475569',
  whiteSpace: 'nowrap',       
  overflow: 'hidden',
  textOverflow: 'ellipsis',   
  textAlign: 'center',
};

  const tableBodyCellSx = (isCurrent) => ({
    fontWeight: 400,
    color: isCurrent ? '#0F172A' : '#475569',
    fontSize: 13,
    py: 1.25,
    textAlign: 'center',
  })

  const tableNumericCellSx = (isCurrent) => ({
    fontWeight: 400,
    ...tableBodyCellSx(isCurrent),
    fontVariantNumeric: 'tabular-nums',
  })

  useEffect(() => {
    const fetchPharmaDetails = async () => {
      try {
        const response = await axios.get('/pharma-details/')
        const data = response.data?.data || {}
        const brands = data.brands || []
        const sources = data.sources || []
        setPharmaDetails({
          brands: brands.length ? brands : defaultBrands,
          sources: sources.length ? sources : defaultSources,
        })
        if (brands.length) setSelectedBrand((prev) => (prev ? prev : brands[0]))
      } catch (err) {
        console.error('Error fetching pharma details:', err)
        setPharmaDetails({ brands: defaultBrands, sources: defaultSources })
      }
    }

    fetchPharmaDetails()
  }, [])

  useEffect(() => {
    const fetchComparison = async () => {
      setLoading(true)
      setError('')
      setPeriods([])
      setComparisonResponse(null)

      try {
        const endDate = asOfDate || today

        const response = await axios.get('/pharma-comparison/', {
          params: { run_date: endDate },
        })

        setComparisonResponse({ ...(response.data || {}), _requested_run_date: endDate })
      } catch (err) {
        console.error('Error fetching pharma comparison:', err)
        setError(err?.response?.data?.error || 'Failed to load comparison data.')
      } finally {
        setLoading(false)
      }
    }

    fetchComparison()
  }, [asOfDate, today])

  const [brandPeriods, setBrandPeriods] = useState([])

  useEffect(() => {
    if (!comparisonResponse) {
      setBrandPeriods([])
      return
    }

    const requestedRunDate = comparisonResponse._requested_run_date || asOfDate || today
    const apiAsOfDate = comparisonResponse.as_of_date || requestedRunDate
    const apiDates = Array.isArray(comparisonResponse.dates) ? comparisonResponse.dates.filter(Boolean) : []
    const apiData = Array.isArray(comparisonResponse.data) ? comparisonResponse.data : []

    const processed = apiData.map(brandBlock => {
      const brand = brandBlock?.brand
      const brandSources = Array.isArray(brandBlock?.sources) ? brandBlock.sources : []

      const inferredDates = brandSources
        .flatMap((sourceItem) => (Array.isArray(sourceItem?.entries) ? sourceItem.entries : []))
        .map((entry) => entry?.run_date)
        .filter(Boolean)

      const effectiveDates = (apiDates.length ? apiDates : [...new Set(inferredDates)])
        .slice()
        .sort((a, b) => String(b).localeCompare(String(a)))
        .slice(0, 4)

      const periods = effectiveDates.map(runDate => {
        const bySource = new Map()
        for (const sourceItem of brandSources) {
          const entry = (sourceItem?.entries || []).find(e => e?.run_date === runDate)
          if (entry) bySource.set(sourceItem.source, { ...entry, source: sourceItem.source })
        }
        return { run_date: runDate, bySource }
      })

      const sourcesList = pharmaDetails.sources?.length ? pharmaDetails.sources : defaultSources
      const periodsBySource = sourcesList.map(source => ({
        source,
        rows: periods.map(p => ({
          run_date: p.run_date,
          entry: p.bySource.get(source)
        }))
      }))

      return {
        brand,
        periodsBySource,
        as_of_date: apiAsOfDate
      }
    })

    setBrandPeriods(processed)
  }, [comparisonResponse, asOfDate, today, pharmaDetails.sources])

  const sources = pharmaDetails.sources?.length ? pharmaDetails.sources : defaultSources

  const periodLabels = ['Latest', 'Previous', 'Prev of Prev', 'Earlier']

  const asOfDisplay = brandPeriods?.[0]?.as_of_date ? formatDisplayDate(brandPeriods[0].as_of_date) : asOfDate ? formatDisplayDate(asOfDate) : ''



const renderParentsChipsCell = (csv, titlePrefix) => {
    const parents = parseParentsCsv(csv)
    if (!parents.length) return '-'

    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
        {parents.map((parent) => (
          <Chip
            key={parent}
            label={parent}
            size="small"
            sx={{

              maxWidth: 180,
              '& .MuiChip-label': { overflow: 'hidden', textOverflow: 'ellipsis',fontSize:"11px" },
            }}
          />
        ))}
      </Box>
    )
  }

  return (
    <Box sx={{ width: '100%', overflow: 'hidden', pb: 2 }}>
      <Paper
        elevation={0}
        sx={{
          p: '18px 20px',
          borderRadius: '16px',
          bgcolor: 'background.paper',
          // border: '1px solid rgba(15, 23, 42, 0.08)',
          mb: 2
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 1,
          }}
        >
          <Typography
            variant="h6"
            sx={{
              fontWeight: 700,
              color: '#1E293B',
              letterSpacing: '-0.02em',

            }}
          >
            Latest 3-Day Comparison
          </Typography>
          {/* <Typography
            variant="body2"
            sx={{
              color: '#64748B',
              fontWeight: 500,
            }}
          >
            Latest data as of {asOfDisplay}
          </Typography> */}
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <TableContainer
          sx={{
            border: '1px solid rgba(15, 23, 42, 0.08)',
            borderRadius: '12px',
            overflowX: 'auto',
            mt:1,maxHeight:"66vh"
          }}
        >
          <Table size="small" stickyHeader>
            <TableHead>
            <TableRow
    sx={{
      bgcolor: '#F8FAFC',
      '& th': {
        position: 'sticky',
        top: 0,
        zIndex: 2,
        background: '#F8FAFC',
      },
    }}
  >
                <TableCell sx={tableHeaderCellSx}>Period</TableCell>
                <TableCell sx={{ ...tableHeaderCellSx, minWidth: 130 }}>Run Date</TableCell>
                <TableCell sx={{ ...tableHeaderCellSx, minWidth: DATE_COL_WIDTH }}>Max Date</TableCell>
                <TableCell align="right" sx={tableHeaderCellSx}>Total TRX</TableCell>
                <TableCell align="right" sx={tableHeaderCellSx}>Total NBRX</TableCell>
                <TableCell align="right" sx={tableHeaderCellSx}>Record Count</TableCell>
                <TableCell align="right" sx={tableHeaderCellSx}>New NBRX</TableCell>
                <TableCell sx={tableHeaderCellSx}>Added Parents</TableCell>
                <TableCell align="right" sx={tableHeaderCellSx}>Removed NBRX</TableCell>
                <TableCell sx={tableHeaderCellSx}>Removed Parents</TableCell>
                <TableCell align="right" sx={tableHeaderCellSx}>Shifted NBRX</TableCell>
                <TableCell sx={tableHeaderCellSx}>Shifted Parents</TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {loading ? (
                <>
                  {[0, 1].map((brandIdx) => (
                    <React.Fragment key={`skeleton-brand-${brandIdx}`}>
                      <TableRow sx={{ bgcolor: '#ffffff' }}>
                        <TableCell colSpan={11} sx={{ py: 3 }}>
                          <Skeleton variant="text" width={180} />
                        </TableCell>
                      </TableRow>
                      {[0, 1, 2].map((idx) => (
                        <TableRow key={`skeleton-row-${brandIdx}-${idx}`}>
                          <TableCell sx={{ pl: 2 }}>
                            <Skeleton variant="rounded" width={80} height={24} />
                          </TableCell>
                          <TableCell><Skeleton variant="text" /></TableCell>
                          <TableCell sx={{ minWidth: DATE_COL_WIDTH }}><Skeleton variant="text" /></TableCell>
                          <TableCell align="right"><Skeleton variant="text" /></TableCell>
                          <TableCell align="right"><Skeleton variant="text" /></TableCell>
                          <TableCell align="right"><Skeleton variant="text" /></TableCell>
                          <TableCell align="right"><Skeleton variant="text" /></TableCell>
                          <TableCell><Skeleton variant="text" /></TableCell>
                          <TableCell align="right"><Skeleton variant="text" /></TableCell>
                          <TableCell><Skeleton variant="text" /></TableCell>
                          <TableCell align="right"><Skeleton variant="text" /></TableCell>
                          <TableCell><Skeleton variant="text" /></TableCell>
                        </TableRow>
                      ))}
                    </React.Fragment>
                  ))}
                </>
              ) : (
                brandPeriods.map((brandBlock) => {
                  const periodsBySource = brandBlock.periodsBySource || []
                  const brandHasAnyData = periodsBySource.some(group => group.rows?.some(r => r?.entry))
                  
                  return (
                    <React.Fragment key={`brand-group-${brandBlock.brand}`}>
                      <TableRow sx={{ bgcolor: '#ffffff' }}>
                        <TableCell
                          colSpan={12}
                          sx={{
                            pt: 4,
                            pb: 1,
                            borderBottom: '1px solid rgba(15, 23, 42, 0.04)',
                          }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <CalendarMonthOutlinedIcon sx={{ fontSize: "16px", color: "#6a6b6d" }} />
                            <Typography sx={{ fontWeight: 600, fontSize: "14px", color: '#1E293B' }}>
                              {asOfDisplay}
                            </Typography>
                            <Box
                              sx={{
                                background: "#0576FC",
                                color: "#fff",
                                borderRadius: "7px",
                                fontSize: "11px",
                                fontWeight: 700,
                                px: 1,
                                py: "3px",
                                textTransform: "uppercase",
                              }}
                            >
                              {brandBlock.brand}
                            </Box>
                          </Box>
                        </TableCell>
                      </TableRow>

                      {!brandHasAnyData ? (
                        <TableRow key={`empty-${brandBlock.brand}`}>
                          <TableCell sx={{ ...tableBodyCellSx(false), py: 3, borderBottom: '1px solid rgba(15, 23, 42, 0.04)' }} colSpan={12} align="center">
                            <Typography variant="body2" sx={{ color: '#94A3B8', fontStyle: 'italic', fontSize: 13 }}>
                              No records found for this period
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ) : (
                        periodsBySource.map((group) => {
                          const rows = group.rows || []
                          const hasAnyRow = rows.some((r) => r?.entry)
                          const isExpanded = !!expandedSources[`${brandBlock.brand}-${group.source}`]
                          const toggleGroup = () => toggleSource(`${brandBlock.brand}-${group.source}`)
                          
                          if (!hasAnyRow) return null;

                          return (
                            <React.Fragment key={`group-${brandBlock.brand}-${group.source}`}>
                              {rows.map((row, idx) => {
                                if (!row?.entry) return null;
                                const isCurrent = idx === 0;
                                const isShown = isCurrent || isExpanded;
                                const label = isCurrent ? 'Latest' : (periodLabels[idx] || `Period ${idx + 1}`);

                                if (!isShown) return null;

                                return (
                                  <TableRow
                                    key={`row-${brandBlock.brand}-${group.source}-${row.run_date}`}
                                    onClick={isCurrent && rows.length > 1 ? toggleGroup : undefined}
                                    sx={{
                                      bgcolor: isCurrent ? '#ffffff' : idx === 1 ? '#FFF7ED' : idx === 2 ? '#EFF6FF' : '#ffffff',
                                      '& td': { borderBottom: '1px solid rgba(15, 23, 42, 0.04)' },
                                      cursor: isCurrent && rows.length > 1 ? 'pointer' : 'default',
                                    }}
                                  >
                                    <TableCell sx={{ ...tableBodyCellSx(isCurrent), pl: 2 }}>
                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        {isCurrent && rows.length > 1 && (
                                          <IconButton
                                            size="small"
                                            sx={{
                                              p: 0,
                                              color: '#2563EB',
                                              transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                                              transition: 'transform 0.2s',
                                            }}
                                          >
                                            <KeyboardArrowDownIcon fontSize="small" />
                                          </IconButton>
                                        )}

                                        {isCurrent ? (
                                          <Box sx={{
                                            display: 'inline-flex',
                                            bgcolor: '#F1F5F9',
                                            px: 1, py: 0.5,
                                            borderRadius: '6px',
                                            border: '1px solid #E2E8F0',
                                            fontSize: 11,
                                            fontWeight: 700,
                                            color: '#475569',
                                            textTransform: 'uppercase',
                                          }}>
                                            {group.source}
                                          </Box>
                                        ) : (
                                          <Box sx={{
                                            display: 'inline-flex',
                                            px: 1, py: 0.3,
                                            borderRadius: '6px',
                                            fontSize: 11,
                                            fontWeight: 600,
                                            ml: 4,
                                            bgcolor: idx === 1 ? '#FFF7ED' : '#EFF6FF',
                                            color: idx === 1 ? '#EA580C' : '#2563EB',
                                            border: idx === 1 ? '1px solid #FDBA74' : '1px solid #93C5FD',
                                          }}>
                                            {label}
                                          </Box>
                                        )}
                                      </Box>
                                    </TableCell>
                                    <TableCell sx={{ ...tableBodyCellSx(isCurrent), fontSize: 12, fontFamily: 'monospace' }}>
                                      {row?.run_date ? formatDisplayDate(row.run_date) : "-"}
                                    </TableCell>
                                    <TableCell sx={{ ...tableBodyCellSx(isCurrent), fontSize: 12, fontFamily: 'monospace' }}>
                                      {row.entry.max_date || '-'}
                                    </TableCell>
                                    <TableCell align="right" sx={tableNumericCellSx(isCurrent)}>{formatNumber(row.entry.total_trx) ?? '-'}</TableCell>
                                    <TableCell align="right" sx={tableNumericCellSx(isCurrent)}>{formatNumber(row.entry.total_nbrx) ?? '-'}</TableCell>
                                    <TableCell align="right" sx={tableNumericCellSx(isCurrent)}>{formatNumber(row.entry.record_count) ?? '-'}</TableCell>
                                    <TableCell align="right" sx={tableNumericCellSx(isCurrent)}>{formatNumber(row.entry.newly_added_nbrx) ?? '-'}</TableCell>
                                    <TableCell sx={{ ...tableBodyCellSx(isCurrent), fontSize: 12 }}>{renderParentsChipsCell(row.entry.added_pharmparents)}</TableCell>
                                    <TableCell align="right" sx={tableNumericCellSx(isCurrent)}>{formatNumber(row.entry.removed_nbrx) ?? '-'}</TableCell>
                                    <TableCell sx={{ ...tableBodyCellSx(isCurrent), fontSize: 12 }}>{renderParentsChipsCell(row.entry.removed_pharmparents)}</TableCell>
                                    <TableCell align="right" sx={tableNumericCellSx(isCurrent)}>{formatNumber(row.entry.shifts_nbrx) ?? '-'}</TableCell>
                                    <TableCell sx={{ ...tableBodyCellSx(isCurrent), fontSize: 12 }}>{renderParentsChipsCell(row.entry.shifted_pharmparents)}</TableCell>
                                  </TableRow>
                                )
                              })}
                            </React.Fragment>
                          )
                        })
                      )}
                    </React.Fragment>
                  )
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>

      </Paper>
    </Box>
  )
}

export default CompareTab
